// ☎️ B345 (AI_150) — TELEFONE BRASILEIRO: formato canônico + aptidão p/ WhatsApp
// ─────────────────────────────────────────────────────────────
// Origem: a investigação do B294 (console do Twilio, 27/08) mostrou pacientes que
// NÃO recebiam a receita — e a causa nunca foi a decoração do número (parênteses,
// traços, espaços: o envio já descarta tudo que não é dígito). Era o número em si:
//   • um TELEFONE FIXO (o do próprio ambulatório) no campo de celular do paciente;
//   • um celular sem conta de WhatsApp (erro 63024 "Invalid message recipient").
//
// ⚠️ CALIBRADO PELOS DADOS, não pela intuição: eu suspeitava que celular ANTIGO
// (8 dígitos, sem o 9) falharia — e vários entregaram normalmente naquele mesmo
// dia. Por isso "celular antigo" NÃO é erro aqui; só o FIXO é, porque fixo não
// tem WhatsApp por definição. Alarme falso ensina o médico a ignorar o aviso.
//
// Nada aqui BLOQUEIA o cadastro: o telefone segue sendo do médico. Isto formata
// ao salvar e AVISA quando o número não vai conseguir receber o documento.
// ─────────────────────────────────────────────────────────────

/** DDDs em uso no Brasil (Anatel). Fora desta lista = digitação errada. */
const DDDS_VALIDOS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

export type TipoTelefone =
  | "celular" // 11 dígitos com 9 na frente — recebe WhatsApp
  | "celular-antigo" // 10 dígitos começando em 6-9 — costuma receber (visto ao vivo)
  | "fixo" // 10 dígitos começando em 2-5 — NÃO recebe WhatsApp
  | "vazio"
  | "invalido";

export interface AnaliseTelefone {
  tipo: TipoTelefone;
  /** Só os dígitos locais (DDD + número), sem o 55. */
  digitos: string;
  /** Pode receber o documento por WhatsApp? */
  aptoWhatsapp: boolean;
  /** Frase curta para a tela; "" quando não há o que avisar. */
  aviso: string;
}

/** Só os dígitos, já sem o código do país. ESPELHA api/_lib/smsClient.js. */
export function digitosTelefone(valor: string | null | undefined): string {
  const d = String(valor ?? "").replace(/\D/g, "");
  if (!d) return "";
  return d.startsWith("55") && d.length > 11 ? d.slice(2) : d;
}

/** Classifica o número e diz se consegue receber WhatsApp. */
export function analisarTelefone(valor: string | null | undefined): AnaliseTelefone {
  const digitos = digitosTelefone(valor);
  if (!digitos) {
    return { tipo: "vazio", digitos: "", aptoWhatsapp: false, aviso: "" };
  }
  if (digitos.length < 10) {
    return {
      tipo: "invalido",
      digitos,
      aptoWhatsapp: false,
      aviso:
        digitos.length === 8 || digitos.length === 9
          ? "Faltou o DDD — sem ele o documento não pode ser enviado."
          : "Número incompleto.",
    };
  }
  if (digitos.length > 11) {
    return { tipo: "invalido", digitos, aptoWhatsapp: false, aviso: "Número com dígitos demais." };
  }
  const ddd = Number(digitos.slice(0, 2));
  if (!DDDS_VALIDOS.has(ddd)) {
    return {
      tipo: "invalido",
      digitos,
      aptoWhatsapp: false,
      aviso: `DDD ${digitos.slice(0, 2)} não existe — confira o número.`,
    };
  }
  const assinante = digitos.slice(2);
  if (assinante.length === 9) {
    // Celular atual: 9 na frente. Qualquer outro dígito é digitação torta.
    if (assinante[0] !== "9") {
      return {
        tipo: "invalido",
        digitos,
        aptoWhatsapp: false,
        aviso: "Celular de 9 dígitos deve começar com 9 — confira o número.",
      };
    }
    return { tipo: "celular", digitos, aptoWhatsapp: true, aviso: "" };
  }
  // 8 dígitos: 2-5 = fixo; 6-9 = celular antigo.
  if (["2", "3", "4", "5"].includes(assinante[0])) {
    return {
      tipo: "fixo",
      digitos,
      aptoWhatsapp: false,
      aviso: "Parece um telefone FIXO — não recebe WhatsApp. Cadastre o celular do paciente.",
    };
  }
  return { tipo: "celular-antigo", digitos, aptoWhatsapp: true, aviso: "" };
}

/**
 * Formato canônico de exibição/gravação: "(69) 98159-1137" | "(69) 3224-2835".
 * O que não for reconhecível volta como veio (nunca destrói o que o médico digitou).
 */
export function formatarTelefone(valor: string | null | undefined): string {
  const bruto = String(valor ?? "").trim();
  // 🔴 B345-FU4 (AI_150) — só formata o que é SÓ um telefone. A equipe anota
  // recado junto do número ("(69) 98159-1137 (recado da vizinha)", "…falar com
  // Maria") e a versão anterior apagava a anotação em silêncio ao sair do campo,
  // porque reconstruía a string a partir dos dígitos. Se sobra qualquer coisa
  // além de dígitos e da pontuação usual de telefone, devolvemos intacto: o
  // formato bonito nunca vale a perda de informação clínica/operacional.
  const soPontuacaoDeTelefone = /^[\d\s()+.\-]*$/.test(bruto);
  if (!soPontuacaoDeTelefone) {
    // B345-FU5 (AI_150) — há texto junto. Ainda dá para arrumar o NÚMERO sem
    // tocar na anotação, DESDE QUE a anotação não tenha dígitos: aí não há
    // ambiguidade sobre onde o telefone termina. Com dígitos na anotação
    // ("ramal 22", um 2º telefone) devolvemos intacto — e o aviso da tela
    // cuida de sinalizar, porque nesse caso o envio de fato não funciona.
    const m = bruto.match(/^([\d\s()+.\-]+?)\s*([^\d]*)$/);
    if (m && m[1] && m[2] && !/\d/.test(m[2])) {
      const numero = formatarTelefone(m[1]);
      if (numero && numero !== m[1].trim()) return `${numero} ${m[2].trim()}`;
    }
    return bruto;
  }
  const { tipo, digitos } = analisarTelefone(valor);
  if (tipo === "vazio") return "";
  if (tipo === "invalido") return bruto;
  const ddd = digitos.slice(0, 2);
  const assinante = digitos.slice(2);
  const corte = assinante.length === 9 ? 5 : 4;
  return `(${ddd}) ${assinante.slice(0, corte)}-${assinante.slice(corte)}`;
}

/**
 * 🔴 B345-FU (AI_150) — TELEFONE VIGENTE do paciente. FONTE ÚNICA.
 *
 * O cadastro guarda o telefone em DOIS campos: `telefone` (o que os formulários
 * de Cadastro/Edição realmente escrevem) e `telefone1` (legado, documentado nos
 * tipos como "alias"). Treze pontos do app liam `telefone1 || telefone` — a
 * ordem ERRADA — e o `telefone1` nunca era atualizado ao editar, porque o
 * formulário só mexe em `telefone`.
 *
 * Consequência real, relatada por Leonardo em 28/08: ele trocou o número no
 * cadastro e o WhatsApp continuou indo para o número ANTIGO, indefinidamente.
 * Grave porque o número velho pode já pertencer a outra pessoa — e o 2º fator
 * (4 últimos dígitos do celular) era calculado sobre o MESMO número antigo, de
 * modo que quem recebesse conseguiria abrir o documento.
 *
 * Preferir `telefone` corrige o comportamento IMEDIATAMENTE, sem migração:
 * quem foi editado tem o valor novo ali; quem é antigo e só tem `telefone1`
 * continua caindo no fallback.
 */
export function telefoneVigente(
  paciente: { telefone?: string | null; telefone1?: string | null } | null | undefined
): string {
  if (!paciente) return "";
  const p = paciente as Record<string, unknown>;
  return String(p.telefone || p.telefone1 || "");
}
